import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getTheme } from '../lib/theme';
import { Shield, Mail, Lock, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { loginEmail, registerEmail, loginGoogle } = useAuth();
  const navigate = useNavigate();
  const t = getTheme(false);

  const [mode, setMode] = useState('login'); // login | register
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nombre, setNombre] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        await registerEmail(email, password, nombre);
      } else {
        await loginEmail(email, password);
      }
      navigate('/');
    } catch (err) {
      const msgs = {
        'auth/user-not-found': 'No existe una cuenta con este correo. Crea una a continuacion.',
        'auth/wrong-password': 'Contraseña incorrecta',
        'auth/invalid-credential': 'No encontramos una cuenta con estos datos. Crea una a continuacion.',
        'auth/email-already-in-use': 'Ya existe una cuenta con este correo. Inicia sesion.',
        'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres',
        'auth/invalid-email': 'Correo electronico invalido',
      };
      setError(msgs[err.code] || err.message);
      // Auto-switch to register if account doesn't exist
      if (mode === 'login' && (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential')) {
        setMode('register');
      }
      // Auto-switch to login if account already exists
      if (mode === 'register' && err.code === 'auth/email-already-in-use') {
        setMode('login');
      }
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      await loginGoogle();
      navigate('/');
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message);
      }
    }
    setLoading(false);
  };

  const inputStyle = { width: '100%', padding: '12px 16px 12px 42px', boxSizing: 'border-box', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, color: '#111827', fontSize: '0.9rem', outline: 'none' };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 50%, #ede9fe 100%)', padding: 20 }}>
      <div style={{ maxWidth: 420, width: '100%' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Shield size={28} style={{ color: '#6366f1' }} />
            <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1e293b' }}>NOM-035</span>
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e293b', margin: '0 0 4px' }}>
            {mode === 'login' ? 'Iniciar sesión' : 'Crear cuenta'}
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.88rem', margin: 0 }}>
            Gestión de riesgos psicosociales
          </p>
        </div>

        {/* Card */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
          {/* Google button */}
          <button onClick={handleGoogle} disabled={loading} style={{
            width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #e5e7eb',
            background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 10, fontSize: '0.9rem', fontWeight: 500, color: '#374151',
            marginBottom: 20,
          }}>
            <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/><path d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9.003 18z" fill="#34A853"/><path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0 5.48 0 2.438 2.017.956 4.958L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/></svg>
            Continuar con Google
          </button>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>o con correo</span>
            <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {mode === 'register' && (
              <div style={{ position: 'relative' }}>
                <input style={{ ...inputStyle, paddingLeft: 16 }} value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Nombre completo" />
              </div>
            )}
            <div style={{ position: 'relative' }}>
              <Mail size={16} style={{ position: 'absolute', left: 14, top: 14, color: '#9ca3af' }} />
              <input style={inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="correo@empresa.com" required />
            </div>
            <div style={{ position: 'relative' }}>
              <Lock size={16} style={{ position: 'absolute', left: 14, top: 14, color: '#9ca3af' }} />
              <input style={{ ...inputStyle, paddingRight: 42 }} type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Contraseña" required minLength={6} />
              <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 12, top: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {error && <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: '0.82rem' }}>{error}</div>}

            <button type="submit" disabled={loading} style={{
              padding: '13px 20px', background: '#6366f1', color: '#fff', border: 'none',
              borderRadius: 10, fontSize: '0.95rem', fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1, marginTop: 4,
            }}>
              {loading ? 'Cargando...' : mode === 'login' ? 'Iniciar sesión' : 'Crear cuenta'}
            </button>
          </form>

          {/* Toggle mode */}
          <div style={{ textAlign: 'center', marginTop: 18, fontSize: '0.85rem', color: '#64748b' }}>
            {mode === 'login' ? '¿No tienes cuenta? ' : '¿Ya tienes cuenta? '}
            <button onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); setLoading(false); }}
              style={{ background: 'none', border: 'none', color: '#6366f1', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
              {mode === 'login' ? 'Regístrate' : 'Inicia sesión'}
            </button>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: '0.75rem', color: '#94a3b8' }}>
          Perspicaz Labs · NOM-035-STPS-2018
        </div>
      </div>
    </div>
  );
}
